import { JSX } from 'preact'
import { useCallback, useState } from 'preact/hooks'
import { useActiveNotification } from './useActiveNotification'

export interface TooltipProps {
  name: string
}

export type TooltipPosition = 'top' | 'left' | 'bottom' | 'right'

const AllTooltipPositions: TooltipPosition[] = ['top', 'left', 'right', 'bottom']

interface UseTooltipPosition {
  ref: (el: HTMLElement) => void
  position: TooltipPosition
}

const getTooltipPosition = (el: HTMLElement, defaultPosition: TooltipPosition = 'top'): TooltipPosition => {
  const parent = el.parentElement
  const container = parent?.closest('.container')
  if (!container) {
    return defaultPosition
  }

  const { width, height } = el.getBoundingClientRect()
  const rect = parent.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const margins: { [key in TooltipPosition]: number } = {
    top: rect.top - containerRect.top,
    left: rect.left - containerRect.left,
    bottom: containerRect.bottom - rect.bottom,
    right: containerRect.right - rect.right,
  }

  return (
    AllTooltipPositions.find(pos => {
      const size = ['top', 'bottom'].includes(pos) ? height : width
      return margins[pos] > size
    }) || defaultPosition
  )
}

const useTooltipPosition = (): UseTooltipPosition => {
  const [position, setPosition] = useState<TooltipPosition>('top')
  const setRef = useCallback((el: HTMLElement) => {
    if (el) {
      setPosition(getTooltipPosition(el))
    }
  }, [])

  return { ref: setRef, position }
}

export const Tooltip = ({ name }: TooltipProps): JSX.Element => {
  const notification = useActiveNotification(name)
  const { ref, position } = useTooltipPosition()
  if (!notification) {
    return null
  }

  return (
    <div ref={ref} className={`tooltip tooltip-${position}`}>
      {notification}
    </div>
  )
}
